/** Spec §14.2 page 6: observed/inferred/unknown confidence labels and budget comparison. */
import { useApps, usePolicies, useUsage } from "../api/hooks.js";
import { StateBadge } from "../components/StateBadge.js";
import { Empty, PageState } from "../components/PageState.js";

export function UsageHistoryPage({ deviceId }: { deviceId: string }) {
  const usage = useUsage(deviceId);
  const policies = usePolicies(deviceId);
  const apps = useApps(deviceId);

  if (!deviceId) return <Empty label="No device selected." />;
  const cap = (policies.data ?? []).find((p) => p.type === "DEVICE_PLAYTIME_DAILY" && p.enabled);
  const usedMinutes = Math.round((usage.data?.observed_seconds ?? 0) / 60);
  const capMinutes = cap ? Number(cap.parameters.minutes ?? 0) : null;
  const nameFor = (pkg: string | null) =>
    pkg ? ((apps.data ?? []).find((a) => a.package_id === pkg)?.display_name ?? pkg) : "unattributed";

  return (
    <PageState query={{ isPending: usage.isPending, error: usage.error }}>
      <div className="grid">
        <div className="card"><h3>Observed playtime</h3><div className="value">{usedMinutes} min</div></div>
        <div className="card"><h3>Daily cap</h3><div className="value">{capMinutes ? `${capMinutes} min` : "not set"}</div></div>
        <div className="card"><h3>Unknown intervals</h3><div className="value">{usage.data?.unknown_intervals ?? 0}</div><div className="hint">gaps are shown, never guessed</div></div>
      </div>
      {(usage.data?.intervals ?? []).length === 0 ? (
        <p className="notice">No usage intervals recorded for this device.</p>
      ) : (
        <table>
          <thead><tr><th>App</th><th>Start</th><th>End</th><th>Confidence</th></tr></thead>
          <tbody>
            {(usage.data?.intervals ?? []).map((i, idx) => (
              <tr key={`${i.started_at}-${idx}`}>
                <td>{nameFor(i.package_id)}</td>
                <td>{new Date(i.started_at).toLocaleString()}</td>
                <td>{new Date(i.ended_at).toLocaleString()}</td>
                <td><StateBadge state={i.confidence} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {capMinutes !== null && usedMinutes > capMinutes ? (
        <p className="notice warn">Observed playtime exceeds the daily cap by {usedMinutes - capMinutes} min.</p>
      ) : null}
    </PageState>
  );
}
